"use client";

import { useRef, useState } from "react";
import { DndContext, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Paper } from "@/components/ui/paper";
import { SkeletonBox, SkeletonLabel } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyOverlay,
  TableFillerRow,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSkeletonBody,
  useTableSkeletonCount,
} from "@/components/ui/table";
import { Toolbar } from "@/components/ui/toolbar";
import { useAuth } from "@/features/auth";
import { Settings } from "@/features/settings/components/layout/settings-page";
import { ApiError } from "@/lib/api/error";
import type { ScorecardDefinitionOut } from "@/lib/api/model";
import { cn } from "@/lib/utils";

import { useCreateLibraryDefinition, useReorderLibrary, useScorecardLibrary, useUpdateLibraryDefinition } from "../hooks/use-library";
import { ArchiveConfirmModal } from "./archive-confirm-modal";
import { type DefinitionPayload, ScorecardDefinitionSheet, toCreateBody, toPatchBody } from "./editor/definition-sheet";
import {
  ArchivedBadge,
  DragHandle,
  RowIconButton,
  fieldCountLabel,
  shortDate,
} from "./shared";
import { useScorecardSensors } from "./sortable";

// The settings column is narrow (900px at most, far less beside the settings
// nav), so "used by" rides in the Name cell's sub-line the way the jobs list
// puts "N Profiles" under a job title, instead of claiming a column.
const NAME_COL = "w-[46%] min-w-0";
const DEFAULT_COL = "w-[16%] whitespace-nowrap";
const UPDATED_COL = "w-[22%] whitespace-nowrap";
const ACTIONS_COL = "w-[16%]";
const ROW_CELL = "!h-14 py-0";
const COLUMN_COUNT = 4;

export function ScorecardLibraryPage() {
  const { isAdmin } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  const lib = useScorecardLibrary(showArchived);
  const create = useCreateLibraryDefinition();
  const update = useUpdateLibraryDefinition();
  const reorder = useReorderLibrary();
  const [sheet, setSheet] = useState<{ open: boolean; definition?: ScorecardDefinitionOut }>({ open: false });
  const [archiveTarget, setArchiveTarget] = useState<ScorecardDefinitionOut | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string> | undefined>();
  const busy = create.isPending || update.isPending || reorder.isPending;
  const sensors = useScorecardSensors();
  const tableRef = useRef<HTMLDivElement>(null);
  const emptyRef = useRef<HTMLDivElement>(null);
  const skeletonCount = useTableSkeletonCount(tableRef, { rowHeight: 56, min: 5 });
  const emptySkeletonCount = useTableSkeletonCount(emptyRef, { rowHeight: 56, min: 5 });

  const rows = lib.data ?? [];
  const active = rows.filter((d) => !d.archived_at);
  const archived = rows.filter((d) => d.archived_at);
  // Archived types are only listed while "Show archived" is on; the archive is
  // opt-in, so an all-archived library still reads as empty by default.
  const visible = showArchived ? [...active, ...archived] : active;
  const loading = lib.isLoading && !lib.data;
  const loadError = lib.isError && !lib.data;
  const emptyLibrary = Boolean(lib.data && active.length === 0 && archived.length === 0);
  const allArchived = Boolean(lib.data && active.length === 0 && archived.length > 0);

  const onSubmit = async (p: DefinitionPayload) => {
    setServerFieldErrors(undefined);
    try {
      if (sheet.definition) {
        await update.mutateAsync({ definitionId: sheet.definition.id, body: toPatchBody(p) });
      } else {
        await create.mutateAsync(toCreateBody(p));
      }
      setServerFieldErrors(undefined);
      // Keep `definition` while the sheet animates out so the form doesn't remount mid-exit.
      setSheet((s) => ({ ...s, open: false }));
    } catch (err) {
      if (err instanceof ApiError && typeof err.detail?.field_key === "string") {
        setServerFieldErrors({ [err.detail.field_key]: err.message });
      }
      /* toasted by the hook */
    }
  };

  const onDragEnd = (e: DragEndEvent) => {
    if (!isAdmin || reorder.isPending || !e.over || e.active.id === e.over.id) return;
    const ids = active.map((d) => d.id);
    const next = arrayMove(ids, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id)));
    reorder.mutate({ ids: next });
  };

  const newTypeButton = (
    <Button
      variant="light"
      size="sm"
      disabled={!isAdmin || busy}
      onClick={() => setSheet({ open: true })}
      icon={<Icon name="IconPlusSmall" size={15} />}
    >
      New type
    </Button>
  );

  return (
    <Settings.Page maxWidth="900px">
      <Settings.Header
        title="Scorecards"
        description="Named scorecard types jobs add from, and the default set a job with none uses."
        action={newTypeButton}
      />
      <Settings.Body gap={4}>
        <Paper.Container className="items-start gap-2.5 md:items-center">
          <Paper.Body>
            <Paper.TitleRow>
              <Icon
                name="IconListSparkle"
                size={14}
                className="shrink-0 text-muted-foreground"
              />
              <Paper.Title>How scorecard types reach a job</Paper.Title>
            </Paper.TitleRow>
            <Paper.Description>
              Jobs with no scorecards of their own use the default set. Adding one to a job makes a job-owned copy
              that remembers where it came from — a library update is offered, never applied. Used by manual fills,
              recorded interviews, and future interview modules.
            </Paper.Description>
          </Paper.Body>
        </Paper.Container>

        {loadError ? (
          <Alert.Container variant="destructive">
            <Alert.Icon>
              <Icon name="IconExclamationCircle" size={16} aria-hidden="true" />
            </Alert.Icon>
            <Alert.Content>Could not load scorecard types.</Alert.Content>
          </Alert.Container>
        ) : (
        <div className="flex flex-col overflow-clip rounded-lg border border-border bg-surface-primary">
          <Toolbar.Container className="justify-between py-1.5">
            <span className="text-sm font-medium text-heading">Scorecard types</span>
            <label className="flex cursor-pointer items-center gap-1 text-[13px] text-contrast/60">
              <Switch
                checked={showArchived}
                onCheckedChange={setShowArchived}
                aria-label="Show archived"
              />
              Show archived
            </label>
          </Toolbar.Container>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <div ref={tableRef} className="flex min-h-[17rem] flex-col">
              {loading ? (
                <Table variant="list" layout="fixed">
                  <LibraryTHead />
                  <LibrarySkeletonRows count={skeletonCount} />
                </Table>
              ) : visible.length === 0 ? (
                <TableEmptyOverlay
                  containerRef={emptyRef}
                  icon="IconListSparkle"
                  title={allArchived ? "All scorecard types are archived." : "No scorecard types yet"}
                  description={
                    allArchived
                      ? "Turn on Show archived to restore one."
                      : "Create your first scorecard type. Mark the ones every job should start with as the default set."
                  }
                  action={emptyLibrary && isAdmin ? newTypeButton : null}
                  backdrop={
                    <Table variant="list" layout="fixed">
                      <LibraryTHead />
                      <LibrarySkeletonRows count={emptySkeletonCount} />
                    </Table>
                  }
                />
              ) : (
                <Table variant="list" layout="fixed">
                  <LibraryTHead sticky />
                  <TableBody>
                    <SortableContext items={active.map((d) => d.id)} strategy={verticalListSortingStrategy}>
                      {visible.map((d) => (
                        <Row
                          key={d.id}
                          d={d}
                          canEdit={isAdmin}
                          busy={busy}
                          onOpen={() => setSheet({ open: true, definition: d })}
                          onToggleDefault={(v) => update.mutate({ definitionId: d.id, body: { is_default: v } })}
                          onArchive={() => setArchiveTarget(d)}
                          onRestore={() =>
                            update.mutate({
                              definitionId: d.id,
                              body: { archived: false },
                              successMessage: "Scorecard type restored.",
                            })
                          }
                        />
                      ))}
                    </SortableContext>
                    <TableFillerRow columns={COLUMN_COUNT} />
                  </TableBody>
                </Table>
              )}
            </div>
          </DndContext>

          <TablePagination
            page={1}
            total={visible.length}
            perPage={Math.max(visible.length, 1)}
            onPageChange={() => {}}
            disabled
            label={(t) => `${t} type${t !== 1 ? "s" : ""}`}
          />
        </div>
        )}

        {lib.data && showArchived && archived.length === 0 && (
          <p className="text-[13px] text-contrast/50">No archived types.</p>
        )}

        <ScorecardDefinitionSheet
          open={sheet.open}
          onOpenChange={(o) => {
            if (!o) setServerFieldErrors(undefined);
            setSheet((s) => ({ ...s, open: o }));
          }}
          mode="library"
          definition={sheet.definition}
          onSubmit={onSubmit}
          busy={busy}
          readOnly={!isAdmin || Boolean(sheet.definition?.archived_at)}
          serverFieldErrors={serverFieldErrors}
        />
        <ArchiveConfirmModal
          target={archiveTarget}
          body="Jobs that copied this type keep their copy; it stops being offered."
          pending={update.isPending}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={async () => {
            if (!archiveTarget) return;
            try {
              await update.mutateAsync({
                definitionId: archiveTarget.id,
                body: { archived: true },
                successMessage: "Scorecard type archived.",
              });
              setArchiveTarget(null);
            } catch {
              /* toasted by the hook */
            }
          }}
        />
      </Settings.Body>
    </Settings.Page>
  );
}

/** Skeleton twin of `Row` — same column widths and 56px row height. */
function LibrarySkeletonRows({ count }: { count: number }) {
  return (
    <TableSkeletonBody
      count={count}
      cellClassName={ROW_CELL}
      renderCells={() => [
        <div key="name" className="flex min-w-0 flex-col gap-1.5">
          <SkeletonLabel className="w-40" />
          <SkeletonLabel tone="muted" className="w-24" />
        </div>,
        <SkeletonBox
          key="default"
          tone="muted"
          className="h-[18px] w-[30px] rounded-full"
        />,
        <SkeletonLabel key="updated" tone="muted" className="w-20" />,
        <SkeletonBox key="actions" tone="muted" className="ml-auto h-4 w-10" />,
      ]}
    />
  );
}

function LibraryTHead({ sticky }: { sticky?: boolean }) {
  return (
    <TableHeader sticky={sticky}>
      <TableRow>
        <TableHead className={NAME_COL}>
          <span className="inline-flex items-center gap-1">
            <Icon name="IconChecklist" size={14} aria-hidden="true" className="text-muted-foreground" />
            Name
          </span>
        </TableHead>
        <TableHead className={DEFAULT_COL}>Default</TableHead>
        <TableHead className={UPDATED_COL}>
          <span className="inline-flex items-center gap-1">
            <Icon name="IconClock" size={14} aria-hidden="true" className="text-muted-foreground" />
            Updated
          </span>
        </TableHead>
        <TableHead className={ACTIONS_COL} aria-label="Actions" />
      </TableRow>
    </TableHeader>
  );
}

function usedByJobsLabel(n: number | null | undefined) {
  const count = n ?? 0;
  return `${count} ${count === 1 ? "job" : "jobs"}`;
}

function Row({
  d,
  canEdit,
  busy,
  onOpen,
  onToggleDefault,
  onArchive,
  onRestore,
}: {
  d: ScorecardDefinitionOut;
  canEdit: boolean;
  busy: boolean;
  onOpen: () => void;
  onToggleDefault: (v: boolean) => void;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const isArchived = Boolean(d.archived_at);
  const sortable = canEdit && !isArchived;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: d.id,
    disabled: !sortable,
  });
  const updated = shortDate(d.updated_at);

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      interactive
      rowLabel={d.name}
      onClick={onOpen}
      className={isArchived ? "opacity-60" : undefined}
    >
      <TableCell className={cn(NAME_COL, ROW_CELL)}>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <Icon
              name="IconChecklist"
              size={14}
              aria-hidden="true"
              className="shrink-0 text-heading opacity-50"
            />
            <span className="min-w-0 truncate text-sm text-contrast/80">{d.name}</span>
            {isArchived && <ArchivedBadge />}
          </div>
          <div className="flex min-w-0 items-center gap-1">
            <Icon
              name="IconBag"
              size={13}
              aria-hidden="true"
              className="shrink-0 text-heading opacity-50"
            />
            <span className="min-w-0 truncate text-[13px] text-contrast/50">
              {fieldCountLabel(d.current_version.fields.length)} ·{" "}
              <span>{usedByJobsLabel(d.used_by_jobs)}</span>
            </span>
          </div>
        </div>
      </TableCell>

      <TableCell
        className={cn(DEFAULT_COL, ROW_CELL)}
        onClick={(e) => e.stopPropagation()}
      >
        <Switch
          aria-label={`Default set — ${d.name}`}
          checked={d.is_default}
          disabled={!canEdit}
          onCheckedChange={onToggleDefault}
        />
      </TableCell>

      <TableCell className={cn(UPDATED_COL, ROW_CELL)}>
        <span className="text-sm text-contrast/60">{updated ?? "—"}</span>
      </TableCell>

      <TableCell
        className={cn(ACTIONS_COL, ROW_CELL, "last:px-3")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end gap-0.5">
          {sortable && (
            <DragHandle
              ref={setActivatorNodeRef}
              label={`Drag to reorder ${d.name}`}
              title="Drag to reorder"
              {...attributes}
              {...listeners}
            />
          )}
          {canEdit &&
            (isArchived ? (
              <RowIconButton label="Restore" title="Restore" disabled={busy} onClick={onRestore}>
                <Icon name="IconArrowRotateClockwise" size={15} aria-hidden="true" />
              </RowIconButton>
            ) : (
              <RowIconButton label="Archive" title="Archive" onClick={onArchive}>
                <Icon name="IconArchive" size={15} aria-hidden="true" />
              </RowIconButton>
            ))}
        </div>
      </TableCell>
    </TableRow>
  );
}
